import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { AddProductVariantSection, TabLabelWithCount } from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ReturnableTypeEnum } from "../returnable-type";
import { hasPricing, resolveInitialStockInMode } from "../sales-return-rules";
import { StockInModeEnum } from "../stock-in-mode";
import { ExpensesSection } from "./expenses-section";
import { GeneralInformationSection } from "./general-information-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { ProductVariantsSection } from "./product-variants-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-return-form-constants";
import { computeSalesReturnTotals } from "./sales-return-totals";
import { SelectDeliveryOrderProductVariantSection } from "./select-delivery-order-product-variant-section";
import { SelectSalesInvoiceProductVariantSection } from "./select-sales-invoice-product-variant-section";

export const SalesReturnForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
}) => {
  const methods = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState, setValue, control } = methods;

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [tabIndex, setTabIndex] = useState(0);

  const [
    returnableType,
    returnableProductVariants,
    salesReturnExpenses,
    warehouse,
    stockInMode,
    seller,
  ] = useWatch({
    control,
    name: [
      "returnableType",
      "returnableProductVariants",
      "salesReturnExpenses",
      "warehouse",
      "stockInMode",
      "seller",
    ],
  });

  // Auto-set stockInMode to FULL when returnableType is NON_INVOICE
  useEffect(() => {
    if (returnableType === ReturnableTypeEnum.NON_INVOICE) {
      setValue("stockInMode", StockInModeEnum.FULL);
    }
  }, [returnableType, setValue]);

  // Changing the source empties the Biaya tab, so don't strand the user on it.
  useEffect(() => {
    setTabIndex(0);
  }, [returnableType]);

  const handleAddProductVariant = (productVariant, product, quantity) => {
    append({
      id: ulid(),
      returnableProductVariantId: null,
      warehouseId: warehouse?.id,
      name: productVariant.name,
      sku: productVariant.sku,
      quantity: Number(quantity),
      unitOfMeasurement: product.unitOfMeasurement.name,
      price: 0,
      discount: 0,
      note: null,
      stockInMode: resolveInitialStockInMode(stockInMode),
    });
  };

  const handleFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeSalesReturnTotals(formData);

    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer.id,
      buyerableType: formData.buyerableType,
      returnableId: formData.returnableId || null,
      returnableType:
        formData.returnableType === ReturnableTypeEnum.NON_INVOICE
          ? null
          : formData.returnableType,
      stockInMode: formData.stockInMode,
      note: formData.note || null,
      taxable: formData.taxable,
      totalIncludesTax: formData.totalIncludesTax,
      taxAmount,
      taxPercentage: formData.taxPercentage,
      // Persist the raw discount input + type so editing can restore it
      // (e.g. a 2% global discount). total_discount is derived by the BE.
      discountType: formData.discountType,
      discountAmount: formData.discountAmount,
      grandTotal,
      // Returned expenses. An invoice return charges back the source invoice's
      // own expense lines (referenced by id, capped server-side); a non-invoice
      // return carries free-form lines that name their own account. The backend
      // rejects expenses outright on a delivery-order return, so send none.
      ...(formData.returnableType !== ReturnableTypeEnum.DELIVERY_ORDER && {
        salesReturnExpenses: (formData.salesReturnExpenses ?? []).map(
          (expense) =>
            expense.salesInvoiceExpenseId
              ? {
                  salesInvoiceExpenseId: expense.salesInvoiceExpenseId,
                  amount: expense.amount,
                  note: expense.note || null,
                }
              : {
                  outletChartOfAccountId: expense.outletChartOfAccount.id,
                  amount: expense.amount,
                  note: expense.note || null,
                }
        ),
      }),
      returnableProductVariants: formData.returnableProductVariants.map(
        (variant) => ({
          returnableProductVariantId: variant.returnableProductVariantId,
          warehouseId: variant.warehouseId || warehouse?.id,
          sku: variant.sku,
          quantity: variant.quantity,
          discount: variant.discount || 0,
          price: variant.price || 0,
          note: variant.note || null,
          stockInMode: variant.stockInMode,
        })
      ),
    };

    await onSubmit(payload);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(handleFormSubmit)}
        onKeyDown={handleKeyDown}
        spacing={2.5}
      >
        <Grid size={12}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Tabs
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
              >
                <Tab
                  label={
                    <TabLabelWithCount
                      label="Barang"
                      count={returnableProductVariants.length}
                    />
                  }
                />
                <Tab
                  label={
                    <TabLabelWithCount
                      label="Biaya"
                      count={salesReturnExpenses.length}
                    />
                  }
                />
              </Tabs>
              <Divider />

              {tabIndex === 0 && (
                <Stack spacing={2.5}>
                  {returnableType === ReturnableTypeEnum.DELIVERY_ORDER && (
                    <SelectDeliveryOrderProductVariantSection />
                  )}
                  {returnableType === ReturnableTypeEnum.SALES_INVOICE && (
                    <SelectSalesInvoiceProductVariantSection />
                  )}
                  {returnableType === ReturnableTypeEnum.NON_INVOICE && (
                    <AddProductVariantSection
                      onAdded={handleAddProductVariant}
                      outletId={seller?.id}
                    />
                  )}

                  <Divider />
                  <Typography variant="h6">Retur Barang</Typography>
                  <ProductVariantsSection />
                </Stack>
              )}

              {tabIndex === 1 && <ExpensesSection />}
            </Stack>
          </Paper>
        </Grid>

        {hasPricing(returnableType) && (
          <>
            <Grid size={{ xs: 12, md: 5, lg: 7 }}>
              <OtherSection editMode={editMode} />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 5 }}>
              <PriceSummarySection editMode={editMode} />
            </Grid>
          </>
        )}

        <Grid
          size={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

SalesReturnForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};
